import type { SerializedEditorState, SerializedLexicalNode } from "lexical";

export interface DocumentContent {
  type: "lexical";
  version: string;
  editorState: SerializedEditorState;
}

export function createDocumentContent(editorState: SerializedEditorState): DocumentContent {
  return {
    type: "lexical",
    version: "1.0.0",
    editorState,
  };
}

export function isValidLexicalState(content: unknown): content is DocumentContent {
  if (!content || typeof content !== "object") return false;
  const obj = content as Record<string, unknown>;
  return (
    obj.type === "lexical" && typeof obj.version === "string" && typeof obj.editorState === "object"
  );
}

export function parseDocumentContent(content: unknown): SerializedEditorState | null {
  if (isValidLexicalState(content)) {
    return content.editorState;
  }

  if (content && typeof content === "object" && "root" in content) {
    return content as SerializedEditorState;
  }

  return null;
}

/**
 * Validates that an object is a valid Lexical SerializedEditorState.
 * Checks for the required root structure with type and children.
 */
export function isValidSerializedEditorState(content: unknown): content is SerializedEditorState {
  if (!content || typeof content !== "object") return false;
  const obj = content as Record<string, unknown>;

  // Must have a root object
  if (!obj.root || typeof obj.root !== "object") return false;

  const root = obj.root as Record<string, unknown>;

  // Root must have type 'root' and children array
  if (root.type !== "root") return false;
  if (!Array.isArray(root.children)) return false;

  return true;
}

export function createEmptyEditorState(): SerializedEditorState {
  return {
    root: {
      children: [
        {
          children: [],
          direction: "ltr",
          format: "",
          indent: 0,
          type: "paragraph",
          version: 1,
          textFormat: 0,
          textStyle: "",
        } as unknown as SerializedLexicalNode,
      ],
      direction: "ltr",
      format: "",
      indent: 0,
      type: "root",
      version: 1,
    },
  };
}

/**
 * Converts plain text into a Lexical SerializedEditorState.
 * Each line becomes a separate paragraph node.
 */
export function textToLexicalState(text: string): SerializedEditorState {
  const lines = text.split("\n");
  const children = lines.map(
    (line) =>
      ({
        children: line
          ? [
              {
                detail: 0,
                format: 0,
                mode: "normal",
                style: "",
                text: line,
                type: "text",
                version: 1,
              } as unknown as SerializedLexicalNode,
            ]
          : [],
        direction: "ltr",
        format: "",
        indent: 0,
        type: "paragraph",
        version: 1,
        textFormat: 0,
        textStyle: "",
      }) as unknown as SerializedLexicalNode,
  );

  return {
    root: {
      children,
      direction: "ltr",
      format: "",
      indent: 0,
      type: "root",
      version: 1,
    },
  };
}

interface TextNode {
  type: string;
  text?: string;
  children?: TextNode[];
}

function extractTextFromNode(node: SerializedLexicalNode): string {
  const textNode = node as unknown as TextNode;

  if (textNode.type === "text" && typeof textNode.text === "string") {
    return textNode.text;
  }

  if (textNode.children && Array.isArray(textNode.children)) {
    return textNode.children
      .map((child) => extractTextFromNode(child as SerializedLexicalNode))
      .join("");
  }

  return "";
}

export function extractPlainText(editorState: SerializedEditorState): string {
  const rootChildren = editorState.root.children as SerializedLexicalNode[];
  return rootChildren
    .map((node) => extractTextFromNode(node))
    .join(" ")
    .trim();
}

export interface TextCounts {
  words: number;
  characters: number;
}

export function countText(text: string): TextCounts {
  const trimmed = text.trim();
  if (!trimmed) {
    return { words: 0, characters: 0 };
  }

  const words = trimmed.split(/\s+/).filter((word) => word.length > 0).length;
  const characters = trimmed.replace(/\s+/g, "").length;

  return { words, characters };
}
